import { api } from './index'

export interface Owner {
  id: string
  name: string
  status: string
  certificates: number
  transfers: number
  scans: number
  registrationDate: string
  age: number
  gender: string | null
  email: string
  phone: string | null
  avatar: string | null
}

export interface OwnerWithCertificates {
  owner: Owner
  certificates: {
    data: any[]
    total: number
    limit: number
    offset: number
  }
}

export interface GetOwnersParams {
  page?: number
  size?: number
  q?: string
  sort?: string
  order?: 'asc' | 'desc'
  status?: string
  gender?: string
  ageMin?: number
  ageMax?: number
  registeredFrom?: string
  registeredTo?: string
}

export interface GetOwnersResponse {
  page: number
  size: number
  totalItems: number
  totalPages: number
  items: Owner[]
}

export interface CreateOwnerManualPayload {
  full_name: string
  // VERA-914 — ISO date string (YYYY-MM-DD); replaces the old derived-from-age field.
  birthday?: string
  gender?: string
  email: string
  phone: string
  // VERA-914 (Stage B) — Details/Address/Marketing sections.
  country?: string
  address_line?: string
  city?: string
  state?: string
  postal_code?: string
  address_country?: string
  // VERA-914 — geocoding from the AddressField autocomplete (VRG-99 scan map).
  latitude?: number | null
  longitude?: number | null
  place_id?: string | null
  formatted_address?: string | null
  email_marketing_consent?: boolean
  sms_marketing_consent?: boolean
  invite_owner?: boolean
  photo?: File | null
}

export interface CreateOwnerManualResponse {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  birthday?: string | null
  gender?: string | null
  created_at: string
  avatar?: string | null
}

export interface UpdateOwnerPayload {
  ownerId: string
  fullName: string
  age?: number
  gender?: string
  email: string
  phone: string
  inviteOwner?: boolean
}

export interface UpdateOwnerResponse {
  id: string
  fullName: string
  age?: number
  gender?: string
  email: string
  phone: string
  status: string
  registrationDate: string
  invitationQueued?: boolean
  warnings?: string[]
}

export interface DeleteOwnerResponse {
  id: string
  isDeleted: boolean
  deletedAt: string
  deletedBy: string
  alreadyDeleted: boolean
}

export interface BulkDeleteOwnersPayload {
  ownerIds: string[]
}

export interface BulkDeleteOwnersResponse {
  results: Array<{
    id: string
    status: 'deleted' | 'already_deleted' | 'failed'
    alreadyDeleted: boolean
    deletedAt?: string
    code?: string
    message?: string
  }>
  summary: {
    requested: number
    deleted: number
    alreadyDeleted: number
    failed: number
  }
}

export interface ResendOwnerInvitationPayload {
  ownerId: string
}

export interface ResendOwnerInvitationResponse {
  success: boolean
  message: string
  email: string
}

const buildRequestBody = (payload: CreateOwnerManualPayload): FormData | Omit<CreateOwnerManualPayload, 'photo'> => {
  const { photo, ...rest } = payload

  if (photo instanceof File) {
    const formData = new FormData()

    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return
      }

      // FormData automatically converts values to strings, but we need to ensure proper formatting
      // For numbers and booleans, convert to string explicitly
      formData.append(key, String(value))
    })

    formData.append('photo', photo)
    return formData
  }

  return rest
}

export const ownerApi = {
  getOwners: async (params: GetOwnersParams): Promise<GetOwnersResponse> => {
    const response = await api.get('/owners', { params })
    return response.data
  },

  getOwnerById: async (ownerId: string, params?: { limit?: number; offset?: number }): Promise<OwnerWithCertificates> => {
    const response = await api.get(`/owners/${ownerId}`, { params })
    return response.data
  },

  createManualOwner: async (payload: CreateOwnerManualPayload): Promise<CreateOwnerManualResponse> => {
    const data = buildRequestBody(payload)
    const response = await api.post('/owners/admin/create-manual', data)
    return response.data
  },

  updateOwner: async (payload: UpdateOwnerPayload): Promise<UpdateOwnerResponse> => {
    const { ownerId, ...data } = payload
    const response = await api.put(`/owners/${ownerId}`, data)
    return response.data
  },

  deleteOwner: async (ownerId: string): Promise<DeleteOwnerResponse> => {
    const response = await api.delete(`/owners/${ownerId}`)
    return response.data
  },

  bulkDeleteOwners: async (payload: BulkDeleteOwnersPayload): Promise<BulkDeleteOwnersResponse> => {
    const response = await api.post('/owners/bulk-delete', payload)
    return response.data
  },

  resendOwnerInvitation: async (payload: ResendOwnerInvitationPayload): Promise<ResendOwnerInvitationResponse> => {
    const response = await api.post(`/owners/${payload.ownerId}/resend-invitation`)
    return response.data
  },
}
